import React from 'react';

// Presentational sections below the hero stage. Copy only uses facts SIKI
// publishes; do not add invented stats here.

const RUN_SHEET = [
  { cue: 'Open', text: 'Open the app and go live. Your phone is the studio.' },
  { cue: 'Play', text: 'Play the records you love and talk between them. Bring your people.' },
  { cue: 'Count', text: 'Every song you play is logged as a stream for every viewer watching.' },
];

const ROLES = [
  {
    side: 'A1',
    name: 'Artists',
    text: 'Get paid when you get played. A live play is a stream, an on-demand play is a stream, and both pay in real time.',
    meta: ['Live royalties', 'On-demand royalties'],
  },
  {
    side: 'A2',
    name: 'Hosts & Curators',
    text: 'Your phone is the station. Build a show around the music you love, get the credit for it, and take a host\'s share of the royalties.',
    meta: ['Host credit', 'Host\'s share'],
  },
  {
    side: 'A3',
    name: 'Listeners',
    text: 'Tune in to people with taste. Find the next record early, and know the artist gets paid for every song you hear.',
    meta: ['Tune in', 'Discover', 'Support'],
  },
];

const pad = (n) => String(n).padStart(2, '0');

const MetaLabel = ({ children, className = '' }) => (
  <div className={`lp-meta ${className}`}>{children}</div>
);

export const Manifesto = ({ platformName, screen }) => (
  <div className="lp-manifesto">
    <div className="lp-rule-row">
      <span>CH. 01</span>
      <span>{platformName} / Live video radio</span>
      <span className="lp-onair"><i aria-hidden="true" />On air</span>
    </div>
    <div className="lp-manifesto__grid">
      <h2 className="lp-display lp-display--xl lp-manifesto__title">
        Every host<br />is a station.
      </h2>
      <div className="lp-manifesto__copy">
        <p className="lp-lede">
          {platformName} is live video radio. Go live from your phone, play the music you love,
          and the artist gets paid while the song is still playing.
        </p>
        <p className="lp-body">
          Music on livestreams never used to count. Here, a live play is a stream.
        </p>
      </div>
      <figure className="lp-manifesto__figure">
        <img src={screen} alt={`${platformName} home screen`} className="lp-phone" />
        <figcaption className="lp-caption">Fig. 01 / Home</figcaption>
      </figure>
    </div>
  </div>
);

export const GoLive = ({ platformName, screens }) => {
  const figures = [
    { src: screens.app2, label: 'Studio', alt: `${platformName} artist studio with track list` },
    { src: screens.app3, label: 'Now playing', alt: `${platformName} now playing screen` },
    { src: screens.app4, label: 'Profile', alt: `${platformName} creator profile` },
  ];
  return (
    <section id="features" className="lp-section lp-golive">
      <div className="lp-golive__head">
        <div>
          <MetaLabel>CH. 02 / Go live</MetaLabel>
          <h2 className="lp-display lp-display--lg">
            No studio needed.<br />Just your phone.
          </h2>
          <p className="lp-lede">Go live from your phone, or bring OBS if you want the full setup.</p>
        </div>
        <ol className="lp-runsheet">
          {RUN_SHEET.map((item, i) => (
            <li key={item.cue}>
              <span className="lp-meta">{pad(i + 1)} {item.cue}</span>
              <p>{item.text}</p>
            </li>
          ))}
        </ol>
      </div>
      <div className="lp-strip">
        {figures.map((fig, i) => (
          <figure className="lp-strip__item" key={fig.label}>
            <img src={fig.src} alt={fig.alt} className="lp-phone" width="560" height="1217" loading="lazy" />
            <figcaption className="lp-caption">Fig. {pad(i + 2)} / {fig.label}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
};

export const Roles = () => (
  <section id="how" className="lp-section lp-roles">
    <div className="lp-roles__intro">
      <MetaLabel>CH. 03 / Who it&apos;s for</MetaLabel>
      <h2 className="lp-display lp-display--lg">Three ways in.</h2>
    </div>
    <ol className="lp-tracklist">
      {ROLES.map((role) => (
        <li className="lp-track" key={role.side}>
          <span className="lp-track__side">{role.side}</span>
          <div className="lp-track__main">
            <h3 className="lp-track__name">{role.name}</h3>
            <p>{role.text}</p>
          </div>
          <ul className="lp-track__meta">
            {role.meta.map((m) => <li key={m}>{m}</li>)}
          </ul>
        </li>
      ))}
    </ol>
  </section>
);

const getDeadlineStatus = (deadlineValue, now) => {
  const deadline = deadlineValue ? new Date(deadlineValue) : null;
  const isExpired = Boolean(deadline && deadline < now);
  let timeLeft = '';
  if (deadline && !isExpired) {
    const diff = deadline - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    timeLeft = days > 0 ? `${days}d ${hours}h left` : `${hours}h left`;
  }
  return { deadline, isExpired, timeLeft };
};

export const PowerPlaysListing = ({ powerPlays }) => {
  const now = new Date();
  return (
    <section id="powerplays" className="lp-section lp-drops">
      <div className="lp-drops__head">
        <div>
          <MetaLabel>CH. 04 / Drops</MetaLabel>
          <h2 className="lp-display lp-display--lg">PowerPlays</h2>
        </div>
        <p className="lp-body">
          Exclusive opportunities for Pro subscribers. Get featured, get in front of the
          industry. Open drops are listed here.
        </p>
      </div>

      <div className="lp-drops__table" role="list">
        <div className="lp-drops__cols" aria-hidden="true">
          <span>No.</span>
          <span>PowerPlay</span>
          <span>Reward</span>
          <span>Deadline</span>
          <span>Status</span>
        </div>

        {powerPlays.length === 0 ? (
          <div className="lp-drops__empty" role="listitem">
            <span className="lp-meta">No open drops</span>
            <p>Nothing open right now. New PowerPlays will be listed here.</p>
          </div>
        ) : (
          powerPlays.map((pp, i) => {
            const { deadline, isExpired, timeLeft } = getDeadlineStatus(pp.deadline, now);
            return (
              <div className={`lp-drop ${isExpired ? 'is-ended' : ''}`} key={pp._id} role="listitem">
                <span className="lp-drop__no">{pad(i + 1)}</span>
                <div className="lp-drop__title">
                  <h3>{pp.title}</h3>
                  {pp.description && <p>{pp.description}</p>}
                </div>
                <div className="lp-drop__reward">
                  <span>Featured artist</span>
                  <span>Industry network</span>
                </div>
                <div className="lp-drop__deadline">
                  <span className="lp-drop__mobile-label">Deadline</span>
                  {deadline
                    ? deadline.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : 'Rolling'}
                </div>
                <div className="lp-drop__status">
                  {timeLeft && <span className="is-live">{timeLeft}</span>}
                  {isExpired && <span>Ended</span>}
                </div>
              </div>
            );
          })
        )}
      </div>

      <p className="lp-drops__note">
        <strong>Subscribe to Pro</strong> to enter PowerPlays.
      </p>
    </section>
  );
};

export const ClosingCta = ({ platformName, onAppStore, onPlayStore }) => (
  <section className="lp-section lp-closing">
    <MetaLabel>{platformName} for iPhone and Android</MetaLabel>
    <h2 className="lp-display lp-display--xl">
      Tune in.<br />Go live.<br /><span className="lp-accent">Get paid.</span>
    </h2>
    <div className="app-buttons">
      <button className="app-store-btn" onClick={onAppStore} aria-label="Download on App Store">
        <span className="small-text">Download on the</span>
        <span className="main-text">App Store</span>
      </button>
      <button className="play-store-btn" onClick={onPlayStore} aria-label="Get it on Google Play">
        <span className="small-text">Get it on</span>
        <span className="main-text">Google Play</span>
      </button>
    </div>
  </section>
);
